import {CSSProperties} from 'react'

import {getTheme, useThemeMode} from '_metronic/partials'

//======================================================================
//  TYPES - Comprehensive TypeScript interfaces for configuration
//======================================================================

/** A single skeleton element (e.g., a line, a box, a circle) */

export enum SkeletonItemType {
  BOX = 'box',
  LINE = 'line',
  CIRCLE = 'circle',
}
type SkeletonItemTypeValue = SkeletonItemType.BOX | SkeletonItemType.LINE | SkeletonItemType.CIRCLE
type SkeletonItem = {
  type: SkeletonItemTypeValue
  width: string | number
  height: string | number
  // Optional style overrides for this specific item
  style?: CSSProperties
}

export enum SkeletonDirection {
  ROW = 'row',
  COLUMN = 'column',
}
type SkeletonDirectionValue = SkeletonDirection.ROW | SkeletonDirection.COLUMN
/** A row container that holds multiple skeleton items, arranged with flexbox */
type SkeletonRow = {
  // A unique key for React's rendering
  key: string
  // Flexbox direction for the items in this row
  direction?: SkeletonDirectionValue
  // Space between items in this row
  gap?: string | number
  // How to align items along the cross-axis (e.g., 'center', 'flex-start')
  alignItems?: CSSProperties['alignItems']
  // How to distribute items along the main-axis
  justifyContent?: CSSProperties['justifyContent']
  // The collection of skeleton items to render inside this row
  items: (SkeletonItem | SkeletonRow)[] // Rows can be nested!
  // Optional style overrides for the row container
  style?: CSSProperties
}

/** The full configuration for a skeleton layout, which is an array of rows */
type SkeletonConfig = SkeletonRow[]

/** Animation type for skeleton elements */
type AnimationType = 'shimmer' | 'pulse' | 'wave'

/** Theme variant for skeleton colors */
type ThemeVariant = 'light' | 'dark' | 'auto'

/** Custom color overrides for specific elements */
type CustomColors = {
  editButtonColor?: string
  deleteButtonColor?: string
  powerButtonColor?: string
  iconColor?: string
  buttonColor?: string
  badgeColor?: string
  dangerColor?: string
}

//======================================================================
//  THEME UTILITIES - Shared theme logic and color palettes
//======================================================================

// Common theme determination logic
const getThemeColors = (theme: ThemeVariant, currentMode?: string, customColors?: CustomColors) => {
  // Determine if we should use dark theme
  let isDark = true // Default to dark
  if (theme === 'light') {
    isDark = false
  } else if (theme === 'dark') {
    isDark = true
  } else if (theme === 'auto') {
    // Use getTheme() for accurate theme detection
    const activeTheme = getTheme()
    isDark = activeTheme === 'dark'
  }

  return {
    isDark,
    // Core colors
    cardBackground: isDark ? '#1D1E24' : '#ffffff',
    cardBorder: isDark ? '#374151' : '#e5e7eb',
    titleColor: isDark ? '#ffffff' : '#C7C7C7FF',
    textColor: isDark ? '#d1d5db' : '#DADADAFF',
    // Accent colors - use custom colors if provided, otherwise use theme defaults
    iconColor: customColors?.iconColor || (isDark ? '#fbbf24' : '#F2B854FF'),
    buttonColor: customColors?.buttonColor || (isDark ? '#fbbf24' : '#F2B854FF'),
    badgeColor: customColors?.badgeColor || (isDark ? '#fbbf24' : '#F2B854FF'),
    // Special colors - use custom colors if provided, otherwise use theme defaults
    editButtonColor: customColors?.editButtonColor || (isDark ? '#3b82f6' : '#1976d2'),
    deleteButtonColor: customColors?.deleteButtonColor || (isDark ? '#ef4444' : '#F56363E6'),
    powerButtonColor: customColors?.powerButtonColor || (isDark ? '#fbbf24' : '#F2B854FF'),
    dangerColor: customColors?.dangerColor || (isDark ? '#ef4444' : '#F56363E6'),
    borderColor: isDark ? '#374151' : '#e5e7eb',
    // Background variants
    itemBackground: isDark ? '#374151' : '#f8f9fa',
    statCardBackground: isDark ? '#374151' : '#f8f9fa',
    filterBackground: isDark ? '#374151' : '#f8f9fa',
    // Neumorphic specific
    neumorphicBackground: isDark ? '#16171c' : '#fafafa',
    neumorphicTitleColor: isDark ? '#515151FF' : '#C7C7C7FF',
    neumorphicTextColor: isDark ? '#404040FF' : '#DADADAFF',
    shadowColor: isDark ? '#000000' : '#d1d5db',
  }
}

// Common skeleton item generators
const createSkeletonItem = (
  type: SkeletonItemTypeValue,
  width: string | number,
  height: string | number,
  style?: CSSProperties
) => ({
  type,
  width,
  height,
  style,
})

const createSkeletonRow = (
  key: string,
  direction: SkeletonDirectionValue = SkeletonDirection.COLUMN,
  gap: string | number = 16,
  alignItems?: CSSProperties['alignItems'],
  justifyContent?: CSSProperties['justifyContent'],
  style?: CSSProperties,
  items: (SkeletonItem | SkeletonRow)[] = []
) => ({
  key,
  direction,
  gap,
  alignItems,
  justifyContent,
  style,
  items,
})

//======================================================================
//  PRE-DEFINED VARIANTS - High-quality layouts inspired by modern UI libraries
//======================================================================

export const CARD_PRESET = {
  CARD: 'card',
  SIMPLE_CARD: 'simple-card',
  BRANCH_CARD: 'branch-card',
  LIST_ITEM_WITH_ACTION_BUTTONS: 'list-item-with-action-buttons',
  LIST_ITEM_WITH_ACTION_BUTTON: 'list-item-with-action-button',
  LIST_ITEM_CARD_WITH_ACTION_BUTTON: 'list-item-card-with-action-button',
  SIMPLE_CARD_WITH_TEXT: 'simple-card-with-text',
  SIMPLE_NOTIFICATION: 'simple-notification',
  PRODUCT_DETAIL: 'product-detail',
  LIST_ITEM: 'list-item',
  ARTICLE: 'article',
  PROFILE_HEADER: 'profile-header',
  TABLE: 'table',
  DASHBOARD: 'dashboard',
  FORM: 'form',
  // Timetable-specific presets
  CONSTRAINT_CATEGORY_CARD: 'constraint-category-card',
  CONSTRAINT_SUBCATEGORY_CARD: 'constraint-subcategory-card',
  CONSTRAINT_LIST_CARD: 'constraint-list-card',
  CONSTRAINT_ITEM: 'constraint-item',
  NEUMORPHIC_STAT_CARD: 'neumorphic-stat-card',
  TIMETABLE_GRID: 'timetable-grid',
  TIMETABLE_EVENT: 'timetable-event',
  TIMETABLE_FILTERS: 'timetable-filters',
  PROGRESS_CHART: 'progress-chart',
  MODAL_FORM: 'modal-form',
  TIMETABLE_PREVALIDATION_MODAL: 'timetable-prevalidation-modal',
  SELECTION_BADGES: 'selection-badges',
  TIMETABLE_VISUALIZATION: 'timetable-visualization',
  SEARCHBAR_WITH_SEARCH_ICON_AND_TEXT: 'searchbar-with-search-icon-and-text',
  DROPDOWN_TEXT_FIELD: 'drop-down-text-field',
  TEXT_FIELD: 'text-field',
  TIMETABLE_GRID_BLUE: 'timetable-grid-blue',
  USER_RATING: 'user-rating',

  TIMETABLE_RECOMMENDATIONS_PANEL: 'timetable-recommendations-panel',
  TIMETABLE_INFO_PANEL: 'timetable-info-panel',
}

const PRESETS: Record<
  string,
  SkeletonConfig | ((theme: ThemeVariant, customColors?: CustomColors) => SkeletonConfig)
> = {
  // Card layout - perfect for content cards, product cards, etc.
  [CARD_PRESET.CARD]: (
    theme: ThemeVariant = 'auto',
    customColors?: CustomColors
  ): SkeletonConfig => {
    const colors = getThemeColors(theme, undefined, customColors)

    return [
      createSkeletonRow(
        'card-root',
        SkeletonDirection.COLUMN,
        16,
        undefined,
        undefined,
        {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '20px',
        },
        [
          createSkeletonItem(SkeletonItemType.BOX, '100%', 180, {borderRadius: '12px'}),
          createSkeletonItem(SkeletonItemType.LINE, '70%', 24, {
            borderRadius: '6px',
            backgroundColor: colors.titleColor,
          }),
          createSkeletonItem(SkeletonItemType.LINE, '90%', 16, {
            borderRadius: '4px',
            backgroundColor: colors.textColor,
          }),
          createSkeletonItem(SkeletonItemType.LINE, '80%', 16, {
            borderRadius: '4px',
            backgroundColor: colors.textColor,
          }),
          createSkeletonRow(
            'card-footer',
            SkeletonDirection.ROW,
            12,
            'center',
            'space-between',
            {marginTop: '8px'},
            [
              createSkeletonItem(SkeletonItemType.LINE, '40%', 14, {
                borderRadius: '4px',
                backgroundColor: colors.textColor,
              }),
              createSkeletonItem(SkeletonItemType.BOX, 80, 32, {
                borderRadius: '8px',
                backgroundColor: colors.buttonColor,
              }),
            ]
          ),
        ]
      ),
    ]
  },
  [CARD_PRESET.SIMPLE_CARD]: (
    theme: ThemeVariant = 'auto',
    customColors?: CustomColors
  ): SkeletonConfig => {
    const colors = getThemeColors(theme, undefined, customColors)

    return [
      createSkeletonRow(
        'card-root',
        SkeletonDirection.COLUMN,
        16,
        undefined,
        undefined,
        {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '20px',
        },
        [createSkeletonItem(SkeletonItemType.BOX, '100%', 80, {borderRadius: '12px'})]
      ),
    ]
  },
  [CARD_PRESET.BRANCH_CARD]: (): SkeletonConfig => {
    return [
      createSkeletonRow(
        'card-root',
        SkeletonDirection.COLUMN,
        0,
        undefined,
        undefined,
        {
          padding: '10px',
        },
        [createSkeletonItem(SkeletonItemType.BOX, '100%', 40, {borderRadius: '12px'})]
      ),
    ]
  },
  [CARD_PRESET.SIMPLE_NOTIFICATION]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      createSkeletonRow(
        'notification-root',
        SkeletonDirection.ROW,
        16,
        'center',
        undefined,
        {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '20px',
        },
        [
          createSkeletonItem(SkeletonItemType.BOX, 48, 48, {
            borderRadius: '8px',
            flexShrink: 0,
            backgroundColor: colors.iconColor,
          }),
          createSkeletonRow(
            'notification-text-col',
            SkeletonDirection.COLUMN,
            8,
            undefined,
            undefined,
            {flexGrow: 1},
            [
              createSkeletonItem(SkeletonItemType.LINE, '65%', 16, {
                borderRadius: '4px',
                backgroundColor: colors.textColor,
              }),
              createSkeletonItem(SkeletonItemType.LINE, '85%', 14, {
                borderRadius: '4px',
                backgroundColor: colors.textColor,
              }),
            ]
          ),
        ]
      ),
    ]
  },
  [CARD_PRESET.SIMPLE_CARD_WITH_TEXT]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      {
        key: 'card-root',
        direction: SkeletonDirection.COLUMN,
        style: {
          width: '100%',
          marginBottom: '8px',
        },
        gap: 0,
        items: [
          // Card content matching the Availability card design
          {
            key: 'card-content',
            direction: SkeletonDirection.COLUMN,
            gap: 10,
            style: {
              width: '100%',
              padding: '20px',
              backgroundColor: colors.cardBackground,
              borderRadius: '6px',
              border: `1px solid ${colors.cardBorder}`,
            },
            items: [
              // Card title "Availability"
              {
                type: SkeletonItemType.LINE,
                width: '45%',
                height: 12,
                style: {
                  borderRadius: '4px',
                  backgroundColor: colors.titleColor,
                  marginBottom: '8px',
                },
              },
              // Content area with text and icons
              {
                key: 'content-area',
                direction: SkeletonDirection.ROW,
                alignItems: 'flex-start' as const,
                style: {
                  width: 'unset',
                  display: 'flex',
                  justifyContent: 'space-between',
                },
                gap: 12,
                items: [
                  // Text content on the left
                  {
                    key: 'text-content',
                    direction: SkeletonDirection.COLUMN,
                    gap: 10,
                    style: {flexGrow: 1, width: 'max-content'},
                    items: [
                      // Start Time line
                      {
                        type: SkeletonItemType.LINE,
                        width: '45%',
                        height: 8,
                        style: {borderRadius: '3px', backgroundColor: colors.textColor},
                      },
                      // End Time line
                      {
                        type: SkeletonItemType.LINE,
                        width: '40%',
                        height: 8,
                        style: {borderRadius: '3px', backgroundColor: colors.textColor},
                      },
                      // Day line
                      {
                        type: SkeletonItemType.LINE,
                        width: '45%',
                        height: 8,
                        style: {borderRadius: '3px', backgroundColor: colors.textColor},
                      },
                      // Teacher line
                      {
                        type: SkeletonItemType.LINE,
                        width: '40%',
                        height: 8,
                        style: {borderRadius: '3px', backgroundColor: colors.textColor},
                      },
                    ],
                  },
                  // Action buttons on the right
                  {
                    key: 'action-buttons',
                    direction: SkeletonDirection.ROW,
                    gap: 8,
                    style: {flexShrink: 0, width: 'fit-content'},
                    items: [
                      // Edit button (yellow pencil icon)
                      {
                        type: SkeletonItemType.BOX,
                        width: 29,
                        height: 29,
                        style: {borderRadius: '6px', backgroundColor: colors.buttonColor},
                      },
                      // Delete button (red trash icon)
                      {
                        type: SkeletonItemType.BOX,
                        width: 29,
                        height: 29,
                        style: {borderRadius: '6px', backgroundColor: colors.deleteButtonColor},
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ]
  },

  [CARD_PRESET.LIST_ITEM_WITH_ACTION_BUTTONS]: (
    theme: ThemeVariant = 'auto',
    customColors?: CustomColors
  ): SkeletonConfig => {
    const colors = getThemeColors(theme, undefined, customColors)

    return [
      {
        key: 'card-root',
        direction: SkeletonDirection.COLUMN,
        style: {
          width: '100%',
          marginBottom: '8px',
        },
        gap: 0,
        items: [
          // Card content matching the Availability card design
          {
            key: 'card-content',
            direction: SkeletonDirection.ROW,
            gap: 10,
            style: {
              width: '100%',
              justifyContent: 'space-between',
              padding: '20px',
              backgroundColor: colors.cardBackground,
              borderRadius: '6px',
              border: `1px solid ${colors.cardBorder}`,
            },
            items: [
              // Card title "Availability"
              {
                type: SkeletonItemType.LINE,
                width: '45%',
                height: 12,
                style: {borderRadius: '4px', backgroundColor: colors.titleColor, margin: '8px 0'},
              },
              // Action buttons on the right
              {
                key: 'action-buttons',
                direction: SkeletonDirection.ROW,
                gap: 8,
                style: {flexShrink: 0, width: 'fit-content'},
                items: [
                  // Edit button (yellow pencil icon)
                  {
                    type: SkeletonItemType.BOX,
                    width: 29,
                    height: 29,
                    style: {borderRadius: '6px', backgroundColor: colors.editButtonColor},
                  },
                  // Delete button (red trash icon)
                  {
                    type: SkeletonItemType.BOX,
                    width: 29,
                    height: 29,
                    style: {borderRadius: '6px', backgroundColor: colors.deleteButtonColor},
                  },
                ],
              },
            ],
          },
        ],
      },
    ]
  },
  // ... after the DROPDOWN_TEXT_FIELD preset

  [CARD_PRESET.USER_RATING]: (
    theme: ThemeVariant = 'auto',
    customColors?: CustomColors
  ): SkeletonConfig => {
    // We get all colors from your centralized utility
    const colors = getThemeColors(theme, undefined, customColors)

    return [
      // We create a root "card" for this component
      createSkeletonRow(
        'rating-root',
        SkeletonDirection.COLUMN,
        12, // gap between lines
        undefined,
        undefined,
        {
          // We can make it look like a card
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '20px',
          width: '100%',
        },
        [
          // Row 1: "Rating" title and "4.5/5" score
          createSkeletonRow(
            'rating-header',
            SkeletonDirection.ROW,
            16, // gap
            'center', // alignItems
            'space-between', // justifyContent
            {marginBottom: 4}, // A little extra space after the header
            [
              // "Rating" title (a faked H5)
              createSkeletonItem(
                SkeletonItemType.LINE,
                '30%',
                20, // Taller, like a title
                {borderRadius: '6px', backgroundColor: colors.titleColor}
              ),
              // "4.5/5" score (a faked sub-label)
              createSkeletonItem(
                SkeletonItemType.LINE,
                '20%',
                16, // Shorter, like a sub-label
                {borderRadius: '4px', backgroundColor: colors.textColor}
              ),
            ]
          ),

          // Row 2: The 5 "Stars"
          createSkeletonRow(
            'rating-stars',
            SkeletonDirection.ROW,
            8, // A small gap between stars
            'center', // alignItems
            undefined,
            undefined,
            [
              // BEHOLD: We fake "stars" using 5 small boxes.
              // We use `colors.iconColor` because you defined it as yellow.
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ...Array.from({length: 5}, (_, i) =>
                createSkeletonItem(
                  SkeletonItemType.BOX, // Just a box!
                  20, // width
                  20, // height
                  {
                    borderRadius: '4px', // A slight radius
                    backgroundColor: colors.iconColor, // The "star" color
                  }
                )
              ),
            ]
          ),

          // Row 3: "Based on 120 reviews" (a faked small-text line)
          createSkeletonItem(SkeletonItemType.LINE, '45%', 14, {
            borderRadius: '4px',
            backgroundColor: colors.textColor,
            marginTop: 8, // A little space from the stars
          }),
        ]
      ),
    ]
  },
  // Make sure this is inside your main PRESETS object

  [CARD_PRESET.LIST_ITEM_WITH_ACTION_BUTTON]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      {
        key: 'card-root',
        direction: SkeletonDirection.COLUMN,
        style: {
          width: '100%',
          marginBottom: '8px',
        },
        gap: 0,
        items: [
          // Card content matching the Availability card design
          {
            key: 'card-content',
            direction: SkeletonDirection.ROW,
            gap: 10,
            style: {
              width: '100%',
              justifyContent: 'space-between',
              padding: '20px',
              backgroundColor: colors.cardBackground,
              borderRadius: '6px',
              border: `1px solid ${colors.cardBorder}`,
            },
            items: [
              // Card title "Availability"
              {
                type: SkeletonItemType.LINE,
                width: '45%',
                height: 12,
                style: {borderRadius: '4px', backgroundColor: colors.titleColor, margin: '8px 0'},
              },
              // Action buttons on the right
              {
                key: 'action-buttons',
                direction: SkeletonDirection.ROW,
                gap: 8,
                style: {flexShrink: 0, width: 'fit-content'},
                items: [
                  // Power button (yellow icon)
                  {
                    type: SkeletonItemType.BOX,
                    width: 29,
                    height: 29,
                    style: {borderRadius: '6px', backgroundColor: colors.buttonColor},
                  },
                ],
              },
            ],
          },
        ],
      },
    ]
  },

  [CARD_PRESET.LIST_ITEM_CARD_WITH_ACTION_BUTTON]: (
    theme: ThemeVariant = 'auto',
    customColors?: CustomColors
  ): SkeletonConfig => {
    const colors = getThemeColors(theme, undefined, customColors)

    return [
      {
        key: 'card-root',
        direction: SkeletonDirection.COLUMN,
        style: {
          width: '100%',
          marginBottom: '8px',
        },
        gap: 0,
        items: [
          // Card content matching the Availability card design
          {
            key: 'card-content',
            direction: SkeletonDirection.ROW,
            gap: 10,
            style: {
              width: '100%',
              justifyContent: 'space-between',
              padding: '20px',
              backgroundColor: colors.cardBackground,
              borderRadius: '6px',
              border: `1px solid ${colors.cardBorder}`,
            },
            items: [
              // Card title "Availability"
              createSkeletonItem(SkeletonItemType.BOX, '100%', 30, {borderRadius: '4px'}),
              // Action buttons on the right
              {
                key: 'action-buttons',
                direction: SkeletonDirection.ROW,
                gap: 8,
                style: {flexShrink: 0, width: 'fit-content'},
                items: [
                  // Power button (yellow icon)
                  {
                    type: SkeletonItemType.BOX,
                    width: 29,
                    height: 29,
                    style: {borderRadius: '6px', backgroundColor: colors.powerButtonColor},
                  },
                ],
              },
            ],
          },
        ],
      },
    ]
  },

  // Product detail layout - complex two-column layout
  [CARD_PRESET.PRODUCT_DETAIL]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      {
        key: 'product-root',
        direction: SkeletonDirection.ROW,
        gap: 32,
        alignItems: 'flex-start',
        style: {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '20px',
        },
        items: [
          // Left side (Image)
          {
            type: SkeletonItemType.BOX,
            width: 300,
            height: 300,
            style: {borderRadius: '16px', flexShrink: 0},
          },
          // Right side (Details)
          {
            key: 'details-col',
            direction: SkeletonDirection.COLUMN,
            gap: 24,
            style: {flexGrow: 1},
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '60%',
                height: 36,
                style: {borderRadius: '8px', backgroundColor: colors.titleColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '30%',
                height: 20,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                key: 'desc-lines',
                direction: SkeletonDirection.COLUMN,
                gap: 12,
                items: [
                  {
                    type: SkeletonItemType.LINE,
                    width: '100%',
                    height: 16,
                    style: {borderRadius: '4px', backgroundColor: colors.textColor},
                  },
                  {
                    type: SkeletonItemType.LINE,
                    width: '100%',
                    height: 16,
                    style: {borderRadius: '4px', backgroundColor: colors.textColor},
                  },
                  {
                    type: SkeletonItemType.LINE,
                    width: '75%',
                    height: 16,
                    style: {borderRadius: '4px', backgroundColor: colors.textColor},
                  },
                ],
              },
              {
                key: 'price-section',
                direction: SkeletonDirection.ROW,
                gap: 16,
                alignItems: 'center',
                style: {marginTop: '8px'},
                items: [
                  {
                    type: SkeletonItemType.LINE,
                    width: '25%',
                    height: 28,
                    style: {borderRadius: '6px', backgroundColor: colors.titleColor},
                  },
                  {
                    type: SkeletonItemType.LINE,
                    width: '20%',
                    height: 20,
                    style: {borderRadius: '4px', backgroundColor: colors.textColor},
                  },
                ],
              },
              {
                type: SkeletonItemType.BOX,
                width: 140,
                height: 48,
                style: {
                  borderRadius: '12px',
                  marginTop: '16px',
                  backgroundColor: colors.buttonColor,
                },
              },
            ],
          },
        ],
      },
    ]
  },

  // List item layout - perfect for user lists, navigation items, etc.
  [CARD_PRESET.LIST_ITEM]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      {
        key: 'list-item-root',
        direction: SkeletonDirection.ROW,
        gap: 16,
        alignItems: 'center',
        style: {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '20px',
        },
        items: [
          {
            type: SkeletonItemType.CIRCLE,
            width: 48,
            height: 48,
            style: {flexShrink: 0, backgroundColor: colors.iconColor},
          },
          {
            key: 'list-item-text',
            direction: SkeletonDirection.COLUMN,
            gap: 8,
            style: {flexGrow: 1},
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '40%',
                height: 20,
                style: {borderRadius: '4px', backgroundColor: colors.titleColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '70%',
                height: 14,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
            ],
          },
          {
            type: SkeletonItemType.BOX,
            width: 24,
            height: 24,
            style: {borderRadius: '4px', flexShrink: 0, backgroundColor: colors.iconColor},
          },
        ],
      },
    ]
  },

  // Article layout - perfect for blog posts, news articles, etc.
  [CARD_PRESET.ARTICLE]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      {
        key: 'article-root',
        direction: SkeletonDirection.COLUMN,
        gap: 24,
        style: {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '20px',
        },
        items: [
          {
            type: SkeletonItemType.LINE,
            width: '80%',
            height: 40,
            style: {borderRadius: '8px', marginBottom: '8px', backgroundColor: colors.titleColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '50%',
            height: 20,
            style: {borderRadius: '4px', backgroundColor: colors.textColor},
          },
          {
            key: 'paragraph-1',
            direction: SkeletonDirection.COLUMN,
            gap: 12,
            style: {marginTop: '20px'},
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '100%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '95%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '98%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '90%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
            ],
          },
          {
            key: 'paragraph-2',
            direction: SkeletonDirection.COLUMN,
            gap: 12,
            style: {marginTop: '16px'},
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '97%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '100%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '92%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
            ],
          },
          {
            key: 'article-footer',
            direction: SkeletonDirection.ROW,
            gap: 16,
            alignItems: 'center',
            style: {marginTop: '20px'},
            items: [
              {
                type: SkeletonItemType.CIRCLE,
                width: 32,
                height: 32,
                style: {backgroundColor: colors.iconColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '30%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '20%',
                height: 14,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
            ],
          },
        ],
      },
    ]
  },

  // Profile header layout - perfect for user profiles, team members, etc.
  [CARD_PRESET.PROFILE_HEADER]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      {
        key: 'profile-root',
        direction: SkeletonDirection.COLUMN,
        gap: 20,
        alignItems: 'center',
        style: {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '20px',
        },
        items: [
          {
            type: SkeletonItemType.CIRCLE,
            width: 120,
            height: 120,
            style: {marginBottom: '8px', backgroundColor: colors.iconColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '60%',
            height: 28,
            style: {borderRadius: '6px', backgroundColor: colors.titleColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '40%',
            height: 18,
            style: {borderRadius: '4px', backgroundColor: colors.textColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '80%',
            height: 16,
            style: {borderRadius: '4px', backgroundColor: colors.textColor},
          },
          {
            key: 'profile-stats',
            direction: SkeletonDirection.ROW,
            gap: 24,
            justifyContent: 'center',
            style: {marginTop: '16px'},
            items: [
              {
                type: SkeletonItemType.LINE,
                width: 60,
                height: 20,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: 60,
                height: 20,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: 60,
                height: 20,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
            ],
          },
        ],
      },
    ]
  },

  // Table layout - perfect for data tables, lists with multiple columns
  [CARD_PRESET.TABLE]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      {
        key: 'table-root',
        direction: SkeletonDirection.COLUMN,
        gap: 16,
        style: {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '20px',
        },
        items: [
          // Table header
          {
            key: 'table-header',
            direction: SkeletonDirection.ROW,
            gap: 16,
            style: {paddingBottom: '12px', borderBottom: `1px solid ${colors.borderColor}`},
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '25%',
                height: 20,
                style: {borderRadius: '4px', backgroundColor: colors.titleColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '25%',
                height: 20,
                style: {borderRadius: '4px', backgroundColor: colors.titleColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '25%',
                height: 20,
                style: {borderRadius: '4px', backgroundColor: colors.titleColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '25%',
                height: 20,
                style: {borderRadius: '4px', backgroundColor: colors.titleColor},
              },
            ],
          },
          // Table rows

          ...Array.from({length: 5}, (_, i) => ({
            key: `table-row-${i}`,
            direction: SkeletonDirection.ROW,
            gap: 16,
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '25%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '25%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '25%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '25%',
                height: 16,
                style: {borderRadius: '4px', backgroundColor: colors.textColor},
              },
            ],
          })),
        ],
      },
    ]
  },

  // Dashboard layout - perfect for dashboard cards and widgets
  [CARD_PRESET.DASHBOARD]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }

    // Theme-specific colors
    const cardBackground = isDark ? '#1D1E24' : '#ffffff'
    const cardBorder = isDark ? '#374151' : '#e5e7eb'
    const titleColor = isDark ? '#ffffff' : '#C7C7C7FF'
    const textColor = isDark ? '#d1d5db' : '#DADADAFF'
    const buttonColor = isDark ? '#fbbf24' : '#F2B854FF'
    const statCardBackground = isDark ? '#374151' : '#f8f9fa'

    return [
      {
        key: 'dashboard-root',
        direction: SkeletonDirection.COLUMN,
        gap: 24,
        style: {
          backgroundColor: cardBackground,
          borderRadius: '12px',
          border: `1px solid ${cardBorder}`,
          padding: '20px',
        },
        items: [
          // Header section
          {
            key: 'dashboard-header',
            direction: SkeletonDirection.ROW,
            gap: 16,
            justifyContent: 'space-between',
            alignItems: 'center',
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '30%',
                height: 32,
                style: {borderRadius: '6px', backgroundColor: titleColor},
              },
              {
                type: SkeletonItemType.BOX,
                width: 120,
                height: 40,
                style: {borderRadius: '8px', backgroundColor: buttonColor},
              },
            ],
          },
          // Stats cards row
          {
            key: 'stats-row',
            direction: SkeletonDirection.ROW,
            gap: 16,
            items: [
              ...Array.from({length: 4}, (_, i) => ({
                key: `stat-card-${i}`,
                direction: SkeletonDirection.COLUMN,
                gap: 12,
                style: {
                  flex: 1,
                  padding: '16px',
                  borderRadius: '12px',
                  backgroundColor: statCardBackground,
                },
                items: [
                  {
                    type: SkeletonItemType.LINE,
                    width: '60%',
                    height: 20,
                    style: {borderRadius: '4px', backgroundColor: textColor},
                  },
                  {
                    type: SkeletonItemType.LINE,
                    width: '40%',
                    height: 32,
                    style: {borderRadius: '6px', backgroundColor: titleColor},
                  },
                  {
                    type: SkeletonItemType.LINE,
                    width: '80%',
                    height: 14,
                    style: {borderRadius: '4px', backgroundColor: textColor},
                  },
                ],
              })),
            ],
          },
          // Chart section
          {
            key: 'chart-section',
            direction: SkeletonDirection.COLUMN,
            gap: 16,
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '25%',
                height: 24,
                style: {borderRadius: '6px', backgroundColor: titleColor},
              },
              {
                type: SkeletonItemType.BOX,
                width: '100%',
                height: 300,
                style: {borderRadius: '12px'},
              },
            ],
          },
        ],
      },
    ]
  },

  // Form layout - perfect for form skeletons
  [CARD_PRESET.FORM]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }

    // Theme-specific colors
    const cardBackground = isDark ? '#1D1E24' : '#ffffff'
    const cardBorder = isDark ? '#374151' : '#e5e7eb'
    const titleColor = isDark ? '#ffffff' : '#C7C7C7FF'
    const textColor = isDark ? '#d1d5db' : '#DADADAFF'
    const buttonColor = isDark ? '#fbbf24' : '#F2B854FF'

    return [
      {
        key: 'form-root',
        direction: SkeletonDirection.COLUMN,
        gap: 20,
        style: {
          backgroundColor: cardBackground,
          borderRadius: '12px',
          border: `1px solid ${cardBorder}`,
          padding: '20px',
        },
        items: [
          {
            type: SkeletonItemType.LINE,
            width: '40%',
            height: 28,
            style: {borderRadius: '6px', marginBottom: '8px', backgroundColor: titleColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '100%',
            height: 48,
            style: {borderRadius: '8px', backgroundColor: textColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '100%',
            height: 48,
            style: {borderRadius: '8px', backgroundColor: textColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '100%',
            height: 120,
            style: {borderRadius: '8px', backgroundColor: textColor},
          },
          {
            key: 'form-buttons',
            direction: SkeletonDirection.ROW,
            gap: 12,
            justifyContent: 'flex-end',
            style: {marginTop: '8px'},
            items: [
              {
                type: SkeletonItemType.BOX,
                width: 80,
                height: 40,
                style: {borderRadius: '8px', backgroundColor: buttonColor},
              },
              {
                type: SkeletonItemType.BOX,
                width: 100,
                height: 40,
                style: {borderRadius: '8px', backgroundColor: buttonColor},
              },
            ],
          },
        ],
      },
    ]
  },

  //======================================================================
  //  TIMETABLE-SPECIFIC PRESETS
  //======================================================================

  // Constraint Category Card - matches ConstraintCategoryCard.tsx layout
  [CARD_PRESET.CONSTRAINT_CATEGORY_CARD]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }
    // Theme-specific colors
    const cardBackground = isDark ? '#1D1E24' : '#ffffff'
    const cardBorder = isDark ? '#374151' : '#e5e7eb'
    const titleColor = isDark ? '#ffffff' : '#C7C7C7FF'
    const textColor = isDark ? '#d1d5db' : '#DADADAFF'
    const iconColor = isDark ? '#fbbf24' : '#F2B854FF'
    const badgeColor = isDark ? '#fbbf24' : '#F2B854FF'
    const borderColor = isDark ? '#374151' : '#e5e7eb'

    return [
      {
        key: 'constraint-category-root',
        direction: SkeletonDirection.COLUMN,
        gap: 0,
        style: {
          backgroundColor: cardBackground,
          borderRadius: '12px',
          border: `1px solid ${cardBorder}`,
        },
        items: [
          // Card header
          {
            key: 'category-header',
            direction: SkeletonDirection.ROW,
            justifyContent: 'space-between',
            alignItems: 'center',
            style: {padding: '20px', borderBottom: `1px solid ${borderColor}`},
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '40%',
                height: 24,
                style: {borderRadius: '6px', backgroundColor: titleColor},
              },
              {
                type: SkeletonItemType.BOX,
                width: 40,
                height: 24,
                style: {borderRadius: '12px', backgroundColor: iconColor},
              },
            ],
          },
          // Category items

          ...Array.from({length: 4}, (_, i) => ({
            key: `category-item-${i}`,
            direction: SkeletonDirection.ROW,
            alignItems: 'center' as const,
            gap: 16,
            style: {padding: '20px', borderBottom: i < 3 ? `1px solid ${borderColor}` : 'none'},
            items: [
              // Symbol/icon
              {
                type: SkeletonItemType.CIRCLE,
                width: 40,
                height: 40,
                style: {flexShrink: 0, backgroundColor: iconColor},
              },
              // Category name
              {
                type: SkeletonItemType.LINE,
                width: '60%',
                height: 18,
                style: {borderRadius: '4px', backgroundColor: textColor},
              },
              // Count badge
              {
                type: SkeletonItemType.BOX,
                width: 30,
                height: 20,
                style: {borderRadius: '10px', flexShrink: 0, backgroundColor: badgeColor},
              },
              // Arrow icon
              {
                type: SkeletonItemType.BOX,
                width: 30,
                height: 30,
                style: {borderRadius: '6px', flexShrink: 0, backgroundColor: iconColor},
              },
            ],
          })),
        ],
      },
    ]
  },

  // Constraint Subcategory Card - matches ConstraintSubCategories.tsx layout
  [CARD_PRESET.CONSTRAINT_SUBCATEGORY_CARD]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      createSkeletonRow(
        'subcategory-root',
        SkeletonDirection.COLUMN,
        0,
        undefined,
        undefined,
        {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          width: '100%',
        },
        [
          // Card header
          createSkeletonRow(
            'subcategory-header',
            SkeletonDirection.ROW,
            0,
            'center',
            undefined,
            {padding: '20px', borderBottom: `1px solid ${colors.borderColor}`},
            [
              createSkeletonItem(SkeletonItemType.LINE, '35%', 24, {
                borderRadius: '6px',
                backgroundColor: colors.titleColor,
              }),
            ]
          ),
          // Subcategory items container with scrollable height
          createSkeletonRow(
            'subcategory-body',
            SkeletonDirection.COLUMN,
            16,
            undefined,
            undefined,
            {
              padding: '20px',
              height: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },

            Array.from({length: 3}, (_, i) =>
              createSkeletonRow(
                `subcategory-item-${i}`,
                SkeletonDirection.ROW,
                16,
                'center',
                undefined,
                {
                  padding: '8px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '16px',
                },
                [
                  createSkeletonItem(SkeletonItemType.BOX, 35, 35, {
                    borderRadius: '8px',
                    flexShrink: 0,
                    backgroundColor: colors.textColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }),
                  createSkeletonRow(
                    `subcategory-text-${i}`,
                    SkeletonDirection.COLUMN,
                    4,
                    undefined,
                    undefined,
                    {flexGrow: 1, minWidth: 0},
                    [
                      createSkeletonItem(SkeletonItemType.LINE, '70%', 16, {
                        borderRadius: '4px',
                        backgroundColor: colors.textColor,
                      }),
                    ]
                  ),
                  createSkeletonItem(SkeletonItemType.BOX, 20, 20, {
                    borderRadius: '6px',
                    flexShrink: 0,
                    backgroundColor: colors.dangerColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }),
                ]
              )
            )
          ),
        ]
      ),
    ]
  },

  // Constraint List Card - matches ConstraintsList.tsx layout
  [CARD_PRESET.CONSTRAINT_LIST_CARD]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }

    // Theme-specific colors
    const cardBackground = isDark ? '#1D1E24' : '#ffffff'
    const cardBorder = isDark ? '#374151' : '#e5e7eb'
    const titleColor = isDark ? '#ffffff' : '#C7C7C7FF'
    const textColor = isDark ? '#d1d5db' : '#DADADAFF'
    const buttonColor = isDark ? '#fbbf24' : '#F2B854FF'
    const itemBackground = isDark ? '#374151' : '#f8f9fa'
    const borderColor = isDark ? '#374151' : '#e5e7eb'

    return [
      {
        key: 'constraint-list-root',
        direction: SkeletonDirection.COLUMN,
        gap: 0,
        style: {
          backgroundColor: cardBackground,
          borderRadius: '12px',
          border: `1px solid ${cardBorder}`,
        },
        items: [
          // Card header with title and add button
          {
            key: 'constraint-list-header',
            direction: SkeletonDirection.ROW,
            justifyContent: 'space-between',
            alignItems: 'center',
            style: {padding: '20px', borderBottom: `1px solid ${borderColor}`},
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '30%',
                height: 24,
                style: {borderRadius: '6px', backgroundColor: titleColor},
              },
              {
                type: SkeletonItemType.BOX,
                width: 40,
                height: 40,
                style: {borderRadius: '8px', backgroundColor: buttonColor},
              },
            ],
          },
          // Constraint items container
          {
            key: 'constraint-items-container',
            direction: SkeletonDirection.COLUMN,
            gap: 16,
            style: {padding: '20px', height: '100px', overflow: 'hidden'},
            items: [
              ...Array.from({length: 2}, (_, i) => ({
                key: `constraint-item-${i}`,
                direction: SkeletonDirection.ROW,
                alignItems: 'flex-start' as const,
                gap: 16,
                style: {padding: '20px', borderRadius: '8px', backgroundColor: itemBackground},
                items: [
                  // Main content area
                  {
                    key: `constraint-content-${i}`,
                    direction: SkeletonDirection.COLUMN,
                    gap: 8,
                    style: {flexGrow: 1},
                    items: [
                      {
                        type: SkeletonItemType.LINE,
                        width: '70%',
                        height: 18,
                        style: {borderRadius: '4px', backgroundColor: textColor},
                      },
                      {
                        type: SkeletonItemType.LINE,
                        width: '50%',
                        height: 14,
                        style: {borderRadius: '4px', backgroundColor: textColor},
                      },
                      {
                        type: SkeletonItemType.LINE,
                        width: '60%',
                        height: 14,
                        style: {borderRadius: '4px', backgroundColor: textColor},
                      },
                    ],
                  },
                  // Action buttons
                  {
                    key: `constraint-actions-${i}`,
                    direction: SkeletonDirection.ROW,
                    gap: 8,
                    style: {flexShrink: 0},
                    items: [
                      {
                        type: SkeletonItemType.BOX,
                        width: 32,
                        height: 32,
                        style: {borderRadius: '6px', backgroundColor: buttonColor},
                      },
                      {
                        type: SkeletonItemType.BOX,
                        width: 32,
                        height: 32,
                        style: {borderRadius: '6px', backgroundColor: buttonColor},
                      },
                    ],
                  },
                ],
              })),
            ],
          },
        ],
      },
    ]
  },

  // Constraint Item - matches ConstraintItem.tsx layout
  [CARD_PRESET.CONSTRAINT_ITEM]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }

    // Theme-specific colors
    // const cardBackground = isDark ? '#1D1E24' : '#ffffff'
    const cardBorder = isDark ? '#374151' : '#e5e7eb'
    const textColor = isDark ? '#d1d5db' : '#DADADAFF'
    const buttonColor = isDark ? '#fbbf24' : '#F2B854FF'
    const itemBackground = isDark ? '#374151' : '#f8f9fa'

    return [
      {
        key: 'constraint-item-root',
        direction: SkeletonDirection.ROW,
        alignItems: 'flex-start',
        gap: 16,
        style: {
          padding: '20px',
          borderRadius: '8px',
          backgroundColor: itemBackground,
          border: `1px solid ${cardBorder}`,
        },
        items: [
          // Main content area
          {
            key: 'constraint-content',
            direction: SkeletonDirection.COLUMN,
            gap: 8,
            style: {flexGrow: 1},
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '70%',
                height: 18,
                style: {borderRadius: '4px', backgroundColor: textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '50%',
                height: 14,
                style: {borderRadius: '4px', backgroundColor: textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '60%',
                height: 14,
                style: {borderRadius: '4px', backgroundColor: textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '45%',
                height: 14,
                style: {borderRadius: '4px', backgroundColor: textColor},
              },
            ],
          },
          // Action buttons
          {
            key: 'constraint-actions',
            direction: SkeletonDirection.ROW,
            gap: 8,
            style: {flexShrink: 0},
            items: [
              {
                type: SkeletonItemType.BOX,
                width: 32,
                height: 32,
                style: {borderRadius: '6px', backgroundColor: buttonColor},
              },
              {
                type: SkeletonItemType.BOX,
                width: 32,
                height: 32,
                style: {borderRadius: '6px', backgroundColor: buttonColor},
              },
            ],
          },
        ],
      },
    ]
  },

  // Neumorphic Stat Card - matches NeumorphicStatCard.tsx layout
  [CARD_PRESET.NEUMORPHIC_STAT_CARD]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      createSkeletonRow(
        'neumorphic-card-root',
        SkeletonDirection.COLUMN,
        16,
        'center',
        undefined,
        {
          width: '100%',
          height: 'fit-content',
          padding: '20px',
          borderRadius: '20px',
          backgroundColor: colors.neumorphicBackground,
          border: `1px solid ${colors.cardBorder}`,
          boxShadow: colors.isDark
            ? `inset 1px 1px 2px ${colors.shadowColor}, inset -1px -1px 2px #2D2D2DFF`
            : `inset 2px 2px 4px ${colors.shadowColor}, inset -2px -2px 4px #ffffff`,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        [
          createSkeletonItem(SkeletonItemType.BOX, 28, 28, {
            backgroundColor: colors.iconColor,
            fontSize: '1.75rem',
          }),
          createSkeletonItem(SkeletonItemType.LINE, '30%', 20, {
            borderRadius: '6px',
            backgroundColor: colors.neumorphicTitleColor,
            fontSize: '2rem',
            fontWeight: 'bold',
          }),
          createSkeletonItem(SkeletonItemType.LINE, '60%', 14, {
            borderRadius: '6px',
            backgroundColor: colors.neumorphicTextColor,
            fontSize: '1rem',
            fontWeight: '600',
          }),
        ]
      ),
    ]
  },

  // Timetable Grid - matches SchedulingGrid.tsx layout with responsive design
  [CARD_PRESET.TIMETABLE_GRID]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }

    // Shadcn-inspired theme colors
    const tableBackground = isDark ? '#0a0a0a' : '#ffffff'
    const headerBackground = '#000066' //isDark ? '#1a1a1a' : '#f8f9fa'
    const stickyColumnBackground = isDark ? '#262626' : '#f1f5f9'
    const cellBackground = isDark ? '#1a1a1a' : '#ffffff'
    const borderColor = isDark ? '#404040' : '#e2e8f0'

    return [
      {
        key: 'timetable-grid-root',
        direction: SkeletonDirection.COLUMN,
        gap: 0,
        style: {
          backgroundColor: tableBackground,
          borderRadius: '8px',
          border: `1px solid ${borderColor}`,
          padding: '0',
          height: '100%',
          width: '100%', // Use full width instead of fit-content
          maxWidth: '100%', // Never exceed the parent/viewport on mobile
          overflowX: 'auto', // Allow horizontal scrolling
          minWidth: 0, // A fixed min-width would overflow narrow flex parents
        },
        items: [
          // Table header row
          {
            key: 'table-header-row',
            direction: SkeletonDirection.ROW,
            gap: 0,
            style: {
              backgroundColor: headerBackground,
              borderBottom: `1px solid ${borderColor}`,
              width: '100%',
              minWidth: '800px', // Ensure minimum width for proper display
            },
            items: [
              // Sticky left column (responsive width)
              {
                type: SkeletonItemType.BOX,
                width: 'clamp(120px, 15vw, 200px)', // Responsive width with min/max
                height: 'clamp(3rem, 8vh, 4rem)', // Responsive height
                style: {
                  backgroundColor: headerBackground,
                  borderRight: `1px solid ${borderColor}`,
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '120px', // Fallback minimum width
                },
              },
              // Additional header columns with responsive widths
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ...Array.from({length: 7}, (_, index) => ({
                type: SkeletonItemType.BOX,
                width: 'clamp(100px, 12vw, 160px)', // Responsive width for time slots
                height: 'clamp(3rem, 8vh, 4rem)', // Responsive height
                style: {
                  backgroundColor: headerBackground,
                  borderRight: `1px solid ${borderColor}`,
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '100px', // Fallback minimum width
                },
              })),
            ],
          },
          // Table body rows

          ...Array.from({length: 8}, (_, rowIndex) => ({
            key: `table-row-${rowIndex}`,
            direction: SkeletonDirection.ROW,
            gap: 0,
            style: {
              borderBottom: rowIndex < 7 ? `1px solid ${borderColor}` : 'none',
              width: '100%',
              minWidth: '800px', // Ensure minimum width for proper display
            },
            items: [
              // Sticky left column (responsive width)
              {
                type: SkeletonItemType.BOX,
                width: 'clamp(120px, 15vw, 200px)', // Responsive width with min/max
                height: 'clamp(4rem, 10vh, 5rem)', // Responsive height
                style: {
                  backgroundColor: stickyColumnBackground,
                  borderRight: `1px solid ${borderColor}`,
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '120px', // Fallback minimum width
                },
              },
              // Additional data columns with responsive widths
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ...Array.from({length: 7}, (_, index) => ({
                type: SkeletonItemType.BOX,
                width: 'clamp(100px, 12vw, 160px)', // Responsive width for time slots
                height: 'clamp(4rem, 10vh, 5rem)', // Responsive height
                style: {
                  backgroundColor: cellBackground,
                  borderRight: `1px solid ${borderColor}`,
                  borderRadius: '0',
                  minWidth: '100px', // Fallback minimum width
                },
              })),
            ],
          })),
        ],
      },
    ]
  },

  // Recommendations panel (purple card in screenshot)
  [CARD_PRESET.TIMETABLE_RECOMMENDATIONS_PANEL]: (
    theme: ThemeVariant = 'auto',
    customColors?: CustomColors
  ): SkeletonConfig => {
    const colors = getThemeColors(theme, undefined, customColors)

    return [
      {
        key: 'timetable-recommendations-root',
        direction: SkeletonDirection.COLUMN,
        gap: 12,
        style: {
          padding: '16px 20px',
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          backgroundColor: colors.cardBackground,
        },
        items: [
          // Header row: icon + title + badge
          {
            key: 'header',
            direction: SkeletonDirection.ROW,
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
            items: [
              {
                key: 'header-left',
                direction: SkeletonDirection.ROW,
                gap: 8,
                alignItems: 'center',
                items: [
                  {
                    type: SkeletonItemType.CIRCLE,
                    width: 20,
                    height: 20,
                  },
                  {
                    type: SkeletonItemType.LINE,
                    width: '40%',
                    height: 18,
                    style: {
                      borderRadius: 4,
                      backgroundColor: colors.titleColor,
                    },
                  },
                ],
              },
              {
                type: SkeletonItemType.BOX,
                width: 80,
                height: 26,
                style: {
                  borderRadius: 999,
                  backgroundColor: colors.badgeColor,
                },
              },
            ],
          },
          // Recommendation line
          {
            key: 'recommendation-main',
            direction: SkeletonDirection.ROW,
            gap: 0,
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '60%',
                height: 16,
                style: {borderRadius: 4, backgroundColor: colors.textColor},
              },
            ],
          },
          // Suggestion text (1–2 lines)
          {
            key: 'recommendation-suggestion',
            direction: SkeletonDirection.COLUMN,
            gap: 6,
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '90%',
                height: 12,
                style: {borderRadius: 4, backgroundColor: colors.textColor},
              },
              {
                type: SkeletonItemType.LINE,
                width: '70%',
                height: 12,
                style: {borderRadius: 4, backgroundColor: colors.textColor},
              },
            ],
          },
        ],
      },
    ]
  },

  // Information bar (blue card with On Days / Off Days / Max Periods)
  [CARD_PRESET.TIMETABLE_INFO_PANEL]: (
    theme: ThemeVariant = 'auto',
    customColors?: CustomColors
  ): SkeletonConfig => {
    const colors = getThemeColors(theme, undefined, customColors)

    const column = (key: string): SkeletonRow => ({
      key,
      direction: SkeletonDirection.COLUMN,
      gap: 6,
      items: [
        {
          type: SkeletonItemType.LINE,
          width: 70,
          height: 14,
          style: {
            borderRadius: 4,
            backgroundColor: colors.titleColor,
          },
        },
        {
          type: SkeletonItemType.LINE,
          width: 140,
          height: 14,
          style: {
            borderRadius: 4,
            backgroundColor: colors.textColor,
          },
        },
      ],
    })

    return [
      {
        key: 'timetable-info-root',
        direction: SkeletonDirection.COLUMN,
        gap: 12,
        style: {
          padding: '16px 20px',
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          backgroundColor: colors.cardBackground,
        },
        items: [
          {
            key: 'info-title',
            direction: SkeletonDirection.ROW,
            gap: 8,
            items: [
              {
                type: SkeletonItemType.LINE,
                width: 120,
                height: 18,
                style: {
                  borderRadius: 4,
                  backgroundColor: colors.titleColor,
                },
              },
            ],
          },
          {
            key: 'info-columns',
            direction: SkeletonDirection.ROW,
            gap: 40,
            // Fixed-width columns (~500px total) overflow narrow screens — wrap instead.
            style: {flexWrap: 'wrap', rowGap: 12, maxWidth: '100%'},
            items: [column('col-on-days'), column('col-off-days'), column('col-max-periods')],
          },
        ],
      },
    ]
  },

  [CARD_PRESET.TIMETABLE_GRID_BLUE]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }

    // Shadcn-inspired theme colors
    const tableBackground = isDark ? '#0a0a0a' : '#ffffff'
    const headerBackground = '#000066'

    const stickyColumnBackground = isDark ? '#262626' : '#f1f5f9'

    const cellBackground = isDark ? '#1a1a1a' : '#ffffff'
    const borderColor = isDark ? '#ffff' : '#121212'

    return [
      {
        key: 'timetable-grid-root',
        direction: SkeletonDirection.COLUMN,
        gap: 0,
        style: {
          backgroundColor: tableBackground,
          borderRadius: '8px',
          border: `1px solid ${borderColor}`,
          padding: '0',
          height: '100%',
          width: '100%', // Use full width instead of fit-content
          maxWidth: '100%', // Never exceed the parent/viewport on mobile
          overflowX: 'auto', // Allow horizontal scrolling
          minWidth: 0, // A fixed min-width would overflow narrow flex parents
        },
        items: [
          // Table header row
          {
            key: 'table-header-row',
            direction: SkeletonDirection.ROW,
            gap: 0,
            style: {
              backgroundColor: headerBackground,
              borderBottom: `0.6px solid ${borderColor}`,
              width: '100%',
              minWidth: '800px', // Ensure minimum width for proper display
            },
            items: [
              // Sticky left column (responsive width)
              {
                type: SkeletonItemType.BOX,
                width: 'clamp(120px, 11.7vw, 147px)', // Responsive width with min/max
                height: 'clamp(2rem, 6vh, 3rem)', // Responsive height
                style: {
                  backgroundColor: headerBackground,
                  borderRight: `0.6px solid ${borderColor}`,
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '120px', // Fallback minimum width
                },
              },

              // Additional header columns with responsive widths
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ...Array.from({length: window.innerWidth % 10}, (_, index) => ({
                type: SkeletonItemType.BOX,
                width: 'clamp(100px, 12vw, 160px)', // Responsive width for time slots
                height: 'clamp(2rem, 6vh, 3rem)', // Responsive height
                style: {
                  backgroundColor: headerBackground,
                  borderRight: `0.6px solid ${borderColor}`,
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '100px', // Fallback minimum width
                },
              })),
            ],
          },
          // Table body rows

          ...Array.from({length: 8}, (_, rowIndex) => ({
            key: `table-row-${rowIndex}`,
            direction: SkeletonDirection.ROW,
            gap: 0,
            style: {
              borderBottom: rowIndex < 7 ? `0.6px solid ${borderColor}` : 'none',
              width: '100%',
              minWidth: '800px', // Ensure minimum width for proper display
            },
            items: [
              // Sticky left column (responsive width)
              {
                type: SkeletonItemType.BOX,
                width: 'clamp(90px, 13vw, 160px)', // Responsive width with min/max
                height: 'clamp(6rem, 13vh, 7rem)', // Responsive height
                style: {
                  backgroundColor: stickyColumnBackground,
                  borderRight: `0.6px solid ${borderColor}`,
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '120px', // Fallback minimum width
                },
              },
              // Additional data columns with responsive widths

              ...Array.from({length: 1}, () => ({
                type: SkeletonItemType.BOX,
                width: 'clamp(100%, 12vw, 160px)', // Responsive width for time slots
                height: 'clamp(6rem, 13vh, 7rem)', // Responsive height
                style: {
                  backgroundColor: cellBackground,
                  borderRight: `0.6px solid ${borderColor}`,
                  borderRadius: '0',
                  minWidth: '100px', // Fallback minimum width
                },
              })),
            ],
          })),
        ],
      },
    ]
  },

  // Timetable Event - matches individual event in grid
  [CARD_PRESET.TIMETABLE_EVENT]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }

    // Theme-specific colors
    const eventBackground = isDark ? '#374151' : '#e3f2fd'
    const eventBorder = isDark ? '#4b5563' : '#bbdefb'
    const textColor = isDark ? '#d1d5db' : '#DADADAFF'

    return [
      {
        key: 'timetable-event-root',
        direction: SkeletonDirection.COLUMN,
        gap: 4,
        style: {
          padding: '8px',
          borderRadius: '8px',
          backgroundColor: eventBackground,
          border: `1px solid ${eventBorder}`,
          minHeight: '60px',
        },
        items: [
          {
            type: SkeletonItemType.LINE,
            width: '90%',
            height: 14,
            style: {borderRadius: '4px', backgroundColor: textColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '70%',
            height: 12,
            style: {borderRadius: '3px', backgroundColor: textColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '50%',
            height: 10,
            style: {borderRadius: '3px', backgroundColor: textColor},
          },
        ],
      },
    ]
  },

  // Timetable Filters - matches Filters.tsx layout
  [CARD_PRESET.TIMETABLE_FILTERS]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }

    // Theme-specific colors
    const cardBorder = isDark ? '#374151' : '#e5e7eb'
    const filterBackground = isDark ? '#374151' : '#f8f9fa'
    const textColor = isDark ? '#d1d5db' : '#DADADAFF'
    const buttonColor = isDark ? '#fbbf24' : '#F2B854FF'

    return [
      {
        key: 'filters-root',
        direction: SkeletonDirection.ROW,
        gap: 16,
        alignItems: 'center',
        style: {
          padding: '16px',
          backgroundColor: filterBackground,
          borderRadius: '8px',
          border: `1px solid ${cardBorder}`,
        },
        items: [
          {
            type: SkeletonItemType.LINE,
            width: '15%',
            height: 40,
            style: {borderRadius: '8px', backgroundColor: textColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '20%',
            height: 40,
            style: {borderRadius: '8px', backgroundColor: textColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '20%',
            height: 40,
            style: {borderRadius: '8px', backgroundColor: textColor},
          },
          {
            type: SkeletonItemType.LINE,
            width: '15%',
            height: 40,
            style: {borderRadius: '8px', backgroundColor: textColor},
          },
          {
            type: SkeletonItemType.BOX,
            width: 100,
            height: 40,
            style: {borderRadius: '8px', backgroundColor: buttonColor},
          },
        ],
      },
    ]
  },

  // Progress Chart - matches ProgressChart.tsx layout
  [CARD_PRESET.PROGRESS_CHART]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }
    // Theme-specific colors
    const cardBackground = isDark ? '#1D1E24' : '#ffffff'
    const cardBorder = isDark ? '#374151' : '#e5e7eb'
    const titleColor = isDark ? '#ffffff' : '#C7C7C7FF'
    const textColor = isDark ? '#d1d5db' : '#DADADAFF'
    const iconColor = isDark ? '#fbbf24' : '#F2B854FF'
    const progressColor = isDark ? '#fbbf24' : '#F2B854FF'

    return [
      {
        key: 'progress-chart-root',
        direction: SkeletonDirection.COLUMN,
        gap: 24,
        style: {
          padding: '24px',
          backgroundColor: cardBackground,
          borderRadius: '12px',
          border: `1px solid ${cardBorder}`,
        },
        items: [
          // Header
          {
            key: 'progress-header',
            direction: SkeletonDirection.ROW,
            justifyContent: 'space-between',
            alignItems: 'center',
            items: [
              {
                type: SkeletonItemType.LINE,
                width: '40%',
                height: 28,
                style: {borderRadius: '6px', backgroundColor: titleColor},
              },
              {
                type: SkeletonItemType.BOX,
                width: 32,
                height: 32,
                style: {borderRadius: '8px', backgroundColor: iconColor},
              },
            ],
          },
          // Progress steps
          {
            key: 'progress-steps',
            direction: SkeletonDirection.COLUMN,
            gap: 16,
            items: [
              ...Array.from({length: 4}, (_, i) => ({
                key: `progress-step-${i}`,
                direction: SkeletonDirection.ROW,
                alignItems: 'center' as const,
                gap: 16,
                items: [
                  // Step indicator
                  {
                    type: SkeletonItemType.CIRCLE,
                    width: 24,
                    height: 24,
                    style: {flexShrink: 0, backgroundColor: iconColor},
                  },
                  // Step content
                  {
                    key: `step-content-${i}`,
                    direction: SkeletonDirection.COLUMN,
                    gap: 4,
                    style: {flexGrow: 1},
                    items: [
                      {
                        type: SkeletonItemType.LINE,
                        width: '60%',
                        height: 16,
                        style: {borderRadius: '4px', backgroundColor: textColor},
                      },
                      {
                        type: SkeletonItemType.LINE,
                        width: '40%',
                        height: 12,
                        style: {borderRadius: '3px', backgroundColor: textColor},
                      },
                    ],
                  },
                  // Progress bar
                  {
                    type: SkeletonItemType.LINE,
                    width: '20%',
                    height: 8,
                    style: {borderRadius: '4px', flexShrink: 0, backgroundColor: progressColor},
                  },
                ],
              })),
            ],
          },
        ],
      },
    ]
  },

  // Modal Form - matches AddConstraint.tsx and Edit.tsx layout
  [CARD_PRESET.MODAL_FORM]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }
    // Theme-specific colors
    const cardBackground = isDark ? '#1D1E24' : '#ffffff'
    const cardBorder = isDark ? '#374151' : '#e5e7eb'
    const titleColor = isDark ? '#ffffff' : '#C7C7C7FF'
    const textColor = isDark ? '#d1d5db' : '#DADADAFF'
    const buttonColor = isDark ? '#fbbf24' : '#F2B854FF'

    return [
      {
        key: 'modal-form-root',
        direction: SkeletonDirection.COLUMN,
        gap: 20,
        style: {
          padding: '24px',
          backgroundColor: cardBackground,
          borderRadius: '12px',
          border: `1px solid ${cardBorder}`,
        },
        items: [
          // Form title
          {
            type: SkeletonItemType.LINE,
            width: '50%',
            height: 24,
            style: {borderRadius: '6px', marginBottom: '8px', backgroundColor: titleColor},
          },
          // Form fields
          {
            key: 'form-fields',
            direction: SkeletonDirection.COLUMN,
            gap: 16,
            items: [
              // Time picker fields
              {
                key: 'time-fields',
                direction: SkeletonDirection.ROW,
                gap: 16,
                items: [
                  {
                    key: 'start-time-field',
                    direction: SkeletonDirection.COLUMN,
                    gap: 8,
                    style: {flex: 1},
                    items: [
                      {
                        type: SkeletonItemType.LINE,
                        width: '30%',
                        height: 16,
                        style: {borderRadius: '4px', backgroundColor: textColor},
                      },
                      {
                        type: SkeletonItemType.LINE,
                        width: '100%',
                        height: 40,
                        style: {borderRadius: '8px', backgroundColor: textColor},
                      },
                    ],
                  },
                  {
                    key: 'end-time-field',
                    direction: SkeletonDirection.COLUMN,
                    gap: 8,
                    style: {flex: 1},
                    items: [
                      {
                        type: SkeletonItemType.LINE,
                        width: '30%',
                        height: 16,
                        style: {borderRadius: '4px', backgroundColor: textColor},
                      },
                      {
                        type: SkeletonItemType.LINE,
                        width: '100%',
                        height: 40,
                        style: {borderRadius: '8px', backgroundColor: textColor},
                      },
                    ],
                  },
                ],
              },
              // Day selector
              {
                key: 'day-selector',
                direction: SkeletonDirection.COLUMN,
                gap: 8,
                items: [
                  {
                    type: SkeletonItemType.LINE,
                    width: '25%',
                    height: 16,
                    style: {borderRadius: '4px', backgroundColor: textColor},
                  },
                  {
                    key: 'day-buttons',
                    direction: SkeletonDirection.ROW,
                    gap: 8,
                    items: [
                      ...Array.from({length: 7}, (_, i) => ({
                        key: `day-button-${i}`,
                        type: SkeletonItemType.CIRCLE,
                        width: 36,
                        height: 36,
                        style: {backgroundColor: buttonColor},
                      })),
                    ],
                  },
                ],
              },
              // Select fields
              {
                key: 'select-fields',
                direction: SkeletonDirection.COLUMN,
                gap: 16,
                items: [
                  {
                    key: 'teacher-select',
                    direction: SkeletonDirection.COLUMN,
                    gap: 8,
                    items: [
                      {
                        type: SkeletonItemType.LINE,
                        width: '20%',
                        height: 16,
                        style: {borderRadius: '4px', backgroundColor: textColor},
                      },
                      {
                        type: SkeletonItemType.LINE,
                        width: '100%',
                        height: 40,
                        style: {borderRadius: '8px', backgroundColor: textColor},
                      },
                    ],
                  },
                  {
                    key: 'subject-select',
                    direction: SkeletonDirection.COLUMN,
                    gap: 8,
                    items: [
                      {
                        type: SkeletonItemType.LINE,
                        width: '20%',
                        height: 16,
                        style: {borderRadius: '4px', backgroundColor: textColor},
                      },
                      {
                        type: SkeletonItemType.LINE,
                        width: '100%',
                        height: 40,
                        style: {borderRadius: '8px', backgroundColor: textColor},
                      },
                    ],
                  },
                ],
              },
            ],
          },
          // Submit button
          {
            key: 'form-submit',
            direction: SkeletonDirection.ROW,
            justifyContent: 'flex-end',
            style: {marginTop: '16px'},
            items: [
              {
                type: SkeletonItemType.BOX,
                width: 80,
                height: 36,
                style: {borderRadius: '8px', backgroundColor: buttonColor},
              },
            ],
          },
        ],
      },
    ]
  },

  // Timetable Pre-validation modal skeleton (used while Re-validating)
  [CARD_PRESET.TIMETABLE_PREVALIDATION_MODAL]: (
    theme: ThemeVariant = 'auto',
    customColors?: CustomColors
  ): SkeletonConfig => {
    const colors = getThemeColors(theme, undefined, customColors)

    return [
      createSkeletonRow(
        'prevalidation-modal-root',
        SkeletonDirection.COLUMN,
        16,
        undefined,
        undefined,
        {
          backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          border: `1px solid ${colors.cardBorder}`,
          padding: '16px',
        },
        [
          // Summary cards (Failed / Warning / Passed)
          createSkeletonRow(
            'prevalidation-summary-cards',
            SkeletonDirection.ROW,
            12,
            'stretch',
            'space-between',
            {width: '100%'},
            [
              createSkeletonItem(SkeletonItemType.BOX, '33%', 64, {
                borderRadius: '10px',
                backgroundColor: colors.itemBackground,
              }),
              createSkeletonItem(SkeletonItemType.BOX, '33%', 64, {
                borderRadius: '10px',
                backgroundColor: colors.itemBackground,
              }),
              createSkeletonItem(SkeletonItemType.BOX, '33%', 64, {
                borderRadius: '10px',
                backgroundColor: colors.itemBackground,
              }),
            ]
          ),

          // Tabs row
          createSkeletonRow(
            'prevalidation-tabs',
            SkeletonDirection.ROW,
            10,
            'center',
            'space-between',
            {
              width: '100%',
              borderRadius: '10px',
              padding: '10px',
              backgroundColor: colors.filterBackground,
            },
            [
              createSkeletonItem(SkeletonItemType.BOX, '13%', 36, {borderRadius: '8px'}),
              createSkeletonItem(SkeletonItemType.BOX, '13%', 36, {borderRadius: '8px'}),
              createSkeletonItem(SkeletonItemType.BOX, '13%', 36, {borderRadius: '8px'}),
              createSkeletonItem(SkeletonItemType.BOX, '13%', 36, {borderRadius: '8px'}),
              createSkeletonItem(SkeletonItemType.BOX, '13%', 36, {borderRadius: '8px'}),
              createSkeletonItem(SkeletonItemType.BOX, '13%', 36, {borderRadius: '8px'}),
              createSkeletonItem(SkeletonItemType.BOX, '13%', 36, {borderRadius: '8px'}),
            ]
          ),

          // Content panel placeholder
          createSkeletonRow(
            'prevalidation-content',
            SkeletonDirection.COLUMN,
            12,
            undefined,
            undefined,
            {
              width: '100%',
              borderRadius: '12px',
              padding: '12px',
              backgroundColor: colors.cardBackground,
              border: `1px solid ${colors.borderColor}`,
              minHeight: 420,
            },
            [
              createSkeletonItem(SkeletonItemType.LINE, '45%', 18, {
                borderRadius: '6px',
                backgroundColor: colors.titleColor,
              }),
              createSkeletonItem(SkeletonItemType.LINE, '85%', 14, {
                borderRadius: '6px',
                backgroundColor: colors.textColor,
              }),
              createSkeletonItem(SkeletonItemType.BOX, '100%', 44, {
                borderRadius: '10px',
                backgroundColor: colors.itemBackground,
                marginTop: '8px',
              }),
              createSkeletonItem(SkeletonItemType.BOX, '100%', 44, {
                borderRadius: '10px',
                backgroundColor: colors.itemBackground,
              }),
              createSkeletonItem(SkeletonItemType.BOX, '100%', 44, {
                borderRadius: '10px',
                backgroundColor: colors.itemBackground,
              }),
              createSkeletonItem(SkeletonItemType.BOX, '100%', 44, {
                borderRadius: '10px',
                backgroundColor: colors.itemBackground,
              }),
            ]
          ),
        ]
      ),
    ]
  },

  // Selection Badges - matches the selection display in Timetable.tsx
  [CARD_PRESET.SELECTION_BADGES]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    const colors = getThemeColors(theme)

    return [
      createSkeletonRow(
        'selection-badges-root',
        SkeletonDirection.ROW,
        12,
        'center',
        'center',
        {
          padding: '1px',
          // backgroundColor: colors.cardBackground,
          borderRadius: '12px',
          // Wrap badges on narrow screens so the skeleton never overflows
          // the viewport on mobile.
          flexWrap: 'wrap',
          maxWidth: '100%',
        },
        [
          // "Current Selection:" label
          createSkeletonRow(
            'branch-badge-1',
            SkeletonDirection.ROW,
            8,
            'center',
            undefined,
            {
              borderRadius: '8px',
              // Badges sit inline (like the real data) and only wrap when the
              // row runs out of space on narrow screens — rows default to
              // width 100%, which would stack each badge on its own line.
              width: 'fit-content',
              flex: '0 0 auto',
            },
            [
              createSkeletonItem(SkeletonItemType.LINE, 70, 12, {
                borderRadius: '4px',
                backgroundColor: colors.textColor,
              }),
            ]
          ),
          createSkeletonRow(
            'branch-badge-2',
            SkeletonDirection.ROW,
            8,
            'center',
            undefined,
            {
              padding: '8px 12px',
              backgroundColor: colors.isDark ? '#374151' : '#e3f2fd',
              borderRadius: '8px',
              width: 'fit-content',
              flex: '0 0 auto',
            },
            [
              createSkeletonItem(SkeletonItemType.CIRCLE, 10, 10, {
                backgroundColor: colors.isDark ? '#3b82f6' : '#1976d2',
              }),
              createSkeletonItem(SkeletonItemType.LINE, 70, 8, {
                borderRadius: '4px',
                backgroundColor: colors.textColor,
              }),
            ]
          ),
          // Level badge (green theme)
          createSkeletonRow(
            'level-badge-1',
            SkeletonDirection.ROW,
            8,
            'center',
            undefined,
            {
              padding: '8px 12px',
              backgroundColor: colors.isDark ? '#374151' : '#e8f5e8',
              borderRadius: '8px',
              width: 'fit-content',
              flex: '0 0 auto',
            },
            [
              createSkeletonItem(SkeletonItemType.CIRCLE, 10, 10, {
                backgroundColor: colors.isDark ? '#10b981' : '#2e7d32',
              }),
              createSkeletonItem(SkeletonItemType.LINE, 70, 8, {
                borderRadius: '4px',
                backgroundColor: colors.textColor,
              }),
            ]
          ),
          createSkeletonRow(
            'level-badge-2',
            SkeletonDirection.ROW,
            8,
            'center',
            undefined,
            {
              padding: '8px 12px',
              backgroundColor: colors.isDark ? '#374151' : '#ECBDFFFF',
              borderRadius: '8px',
              width: 'fit-content',
              flex: '0 0 auto',
            },
            [
              createSkeletonItem(SkeletonItemType.CIRCLE, 10, 10, {
                backgroundColor: colors.isDark ? '#A517DDFF' : '#C87AF6FF',
              }),
              createSkeletonItem(SkeletonItemType.LINE, 70, 8, {
                borderRadius: '4px',
                backgroundColor: colors.textColor,
              }),
            ]
          ),
        ]
      ),
    ]
  },

  // Timetable Visualization skeleton - matches the layout from the image
  [CARD_PRESET.TIMETABLE_VISUALIZATION]: (
    theme: ThemeVariant = 'auto',
    customColors?: CustomColors
  ): SkeletonConfig => {
    const colors = getThemeColors(theme, undefined, customColors)

    return [
      // Legend section
      createSkeletonRow(
        'legend-section',
        SkeletonDirection.ROW,
        16,
        undefined,
        undefined,
        {
          padding: '16px',
          backgroundColor: colors.cardBackground,
          borderRadius: '8px',
          marginBottom: '16px',
        },
        [
          // Legend items row
          createSkeletonRow(
            'legend-items',
            SkeletonDirection.ROW,
            24,
            'center',
            'flex-start',
            {flexWrap: 'wrap'},
            [
              // Legend item 1 - Valid School Hours (green)
              createSkeletonRow(
                'legend-item-1',
                SkeletonDirection.ROW,
                8,
                'center',
                undefined,
                {width: 'min-content'},
                [
                  createSkeletonItem(SkeletonItemType.BOX, 20, 20, {
                    backgroundColor: colors.isDark ? '#1a4d3a' : '#e8f5e8',
                    borderRadius: '4px',
                    border: `1px solid ${colors.cardBorder}`,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 80, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                ]
              ),
              // Legend item 2 - Outside School Hours (brown)
              createSkeletonRow(
                'legend-item-2',
                SkeletonDirection.ROW,
                8,
                'center',
                undefined,
                {width: 'min-content'},
                [
                  createSkeletonItem(SkeletonItemType.BOX, 20, 20, {
                    backgroundColor: colors.isDark ? '#4d3d1a' : '#fff3cd',
                    borderRadius: '4px',
                    border: `1px solid ${colors.cardBorder}`,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 80, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                ]
              ),
              // Legend item 3 - Off Days (gray)
              createSkeletonRow(
                'legend-item-3',
                SkeletonDirection.ROW,
                8,
                'center',
                undefined,
                {width: 'min-content'},
                [
                  createSkeletonItem(SkeletonItemType.BOX, 20, 20, {
                    backgroundColor: colors.isDark ? '#3d3d3d' : '#f8f9fa',
                    borderRadius: '4px',
                    border: `1px solid ${colors.cardBorder}`,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 60, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                ]
              ),
              // Legend item 4 - Break Times (yellow)
              createSkeletonRow(
                'legend-item-4',
                SkeletonDirection.ROW,
                8,
                'center',
                undefined,
                {width: 'min-content'},
                [
                  createSkeletonItem(SkeletonItemType.BOX, 20, 20, {
                    backgroundColor: '#ffc107',
                    borderRadius: '4px',
                    border: `1px solid ${colors.cardBorder}`,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 80, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                ]
              ),
              // Legend item 5 - Constraint Violations (red)
              createSkeletonRow(
                'legend-item-5',
                SkeletonDirection.ROW,
                8,
                'center',
                undefined,
                {width: 'min-content'},
                [
                  createSkeletonItem(SkeletonItemType.BOX, 20, 20, {
                    backgroundColor: colors.isDark ? '#4d1a1a' : '#ffebee',
                    borderRadius: '4px',
                    border: `2px solid ${colors.dangerColor}`,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 150, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                ]
              ),
              // Legend item 6 - Available Lecture Time (light red)
              createSkeletonRow(
                'legend-item-6',
                SkeletonDirection.ROW,
                8,
                'center',
                undefined,
                {width: 'min-content'},
                [
                  createSkeletonItem(SkeletonItemType.BOX, 20, 20, {
                    backgroundColor: colors.isDark ? '#4d1a1a' : '#ffebee',
                    borderRadius: '4px',
                    border: `1px solid ${colors.cardBorder}`,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 160, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                ]
              ),
              // Legend item 7 - Saved Lecture Slots (blue)
              createSkeletonRow(
                'legend-item-7',
                SkeletonDirection.ROW,
                8,
                'center',
                undefined,
                {width: 'min-content'},
                [
                  createSkeletonItem(SkeletonItemType.BOX, 20, 20, {
                    backgroundColor: colors.isDark ? '#1a4d7a' : '#e3f2fd',
                    borderRadius: '4px',
                    border: `1px solid ${colors.cardBorder}`,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 140, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                ]
              ),
            ]
          ),
        ]
      ),

      // Information panel
      createSkeletonRow(
        'info-panel',
        SkeletonDirection.COLUMN,
        16,
        undefined,
        undefined,
        {
          padding: '16px',
          backgroundColor: colors.cardBackground,
          borderRadius: '8px',
          marginBottom: '16px',
          border: `1px solid ${colors.isDark ? '#3d3d3d' : '#bee5eb'}`,
        },
        [
          // Information title
          createSkeletonItem(SkeletonItemType.LINE, 100, 20, {
            borderRadius: '4px',
            backgroundColor: colors.titleColor,
          }),
          // Information content in two columns
          createSkeletonRow(
            'info-content',
            SkeletonDirection.ROW,
            16,
            'flex-start',
            'space-between',
            undefined,
            [
              // Left column
              createSkeletonRow(
                'info-left',
                SkeletonDirection.COLUMN,
                8,
                undefined,
                undefined,
                {flex: 1, marginRight: '16px'},
                [
                  createSkeletonItem(SkeletonItemType.LINE, 120, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 100, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 110, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 130, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 140, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 100, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                ]
              ),
              // Right column
              createSkeletonRow(
                'info-right',
                SkeletonDirection.COLUMN,
                8,
                undefined,
                undefined,
                {flex: 1},
                [
                  createSkeletonItem(SkeletonItemType.LINE, 80, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 90, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 120, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                  createSkeletonItem(SkeletonItemType.LINE, 110, 14, {
                    borderRadius: '4px',
                    backgroundColor: colors.textColor,
                  }),
                ]
              ),
            ]
          ),
        ]
      ),

      // Timetable grid (nested preset)
      CARD_PRESET.TIMETABLE_GRID as unknown as any,
      // Constraint violations section
      createSkeletonRow(
        'constraint-violations',
        SkeletonDirection.COLUMN,
        16,
        undefined,
        undefined,
        {
          padding: '16px',
          backgroundColor: colors.cardBackground,
          borderRadius: '8px',
          border: `1px solid ${colors.cardBorder}`,
          margin: '16px 0',
        },
        [
          // Section header with badge
          createSkeletonRow(
            'violations-header',
            SkeletonDirection.ROW,
            16,
            'center',
            'space-between',
            undefined,
            [
              createSkeletonItem(SkeletonItemType.LINE, 200, 20, {
                borderRadius: '4px',
                backgroundColor: colors.titleColor,
              }),
              createSkeletonItem(SkeletonItemType.BOX, 80, 24, {
                borderRadius: '12px',
                backgroundColor: colors.dangerColor,
              }),
            ]
          ),
          // Violation items

          ...Array.from({length: 2}, (_, i) =>
            createSkeletonRow(
              `violation-item-${i}`,
              SkeletonDirection.ROW,
              8,
              'flex-start',
              undefined,
              {
                padding: '12px',
                backgroundColor: colors.isDark ? '#4d1a1a' : '#ffebee',
                borderRadius: '8px',
                marginBottom: '8px',
                border: `1px solid ${colors.dangerColor}`,
              },
              [
                createSkeletonItem(SkeletonItemType.CIRCLE, 16, 16, {
                  backgroundColor: colors.dangerColor,
                }),
                createSkeletonRow(
                  `violation-content-${i}`,
                  SkeletonDirection.COLUMN,
                  4,
                  undefined,
                  undefined,
                  {flex: 1, marginLeft: '8px'},
                  [
                    createSkeletonItem(SkeletonItemType.LINE, '90%', 14, {
                      borderRadius: '4px',
                      backgroundColor: colors.textColor,
                    }),
                    createSkeletonItem(SkeletonItemType.LINE, '80%', 12, {
                      borderRadius: '4px',
                      backgroundColor: colors.textColor,
                    }),
                  ]
                ),
              ]
            )
          ),
        ]
      ),

      // Action buttons
      // createSkeletonRow(
      //   'action-buttons',
      //   'row',
      //   12,
      //   'center',
      //   'flex-end',
      //   {
      //     padding: '16px',
      //     backgroundColor: colors.cardBackground,
      //     borderRadius: '8px',
      //     border: `1px solid ${colors.cardBorder}`,
      //   },
      //   [
      //     createSkeletonItem('box', 80, 36, {
      //       borderRadius: '6px',
      //       backgroundColor: colors.isDark ? '#374151' : '#e5e7eb',
      //     }),
      //     createSkeletonItem('box', 120, 36, {
      //       borderRadius: '6px',
      //       backgroundColor: colors.isDark ? '#374151' : '#e5e7eb',
      //     }),
      //     createSkeletonItem('box', 80, 36, {
      //       borderRadius: '6px',
      //       backgroundColor: colors.isDark ? '#374151' : '#e5e7eb',
      //     }),
      //     createSkeletonItem('box', 120, 36, {
      //       borderRadius: '6px',
      //       backgroundColor: colors.isDark ? '#10b981' : '#2e7d32',
      //     }),
      //   ]
      // ),
    ]
  },

  // Searchbar with Search Icon and Text - matches the searchbar in ConstraintsList.tsx
  [CARD_PRESET.SEARCHBAR_WITH_SEARCH_ICON_AND_TEXT]: (
    theme: ThemeVariant = 'auto'
  ): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }
    // Theme-specific colors
    const searchbarBackground = isDark ? '#1D1E24' : '#ffffff'
    const searchbarBorder = isDark ? '#374151' : '#e5e7eb'
    const iconColor = isDark ? '#A1A5B7' : '#6b7280'
    // const textColor = isDark ? '#A1A5B7' : '#9ca3af'

    return [
      {
        key: 'searchbar-root',
        direction: SkeletonDirection.ROW,
        alignItems: 'center',
        gap: 0,
        style: {
          padding: '4px 26px', // px-8 equivalent
          backgroundColor: 'transparent',
          width: '100%',
        },
        items: [
          {
            key: 'searchbar-container',
            direction: SkeletonDirection.ROW,
            alignItems: 'center',
            gap: 0,
            style: {
              position: 'relative',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
            },
            items: [
              // Search icon (positioned absolutely on the left)
              {
                key: 'search-icon',
                direction: SkeletonDirection.ROW,
                alignItems: 'center',
                justifyContent: 'center',
                style: {
                  position: 'absolute',
                  left: '12px', // ms-3 equivalent
                  zIndex: 10,
                  width: 16,
                  height: 16,
                  color: iconColor,

                  fontSize: '14px',
                },
                items: [
                  {
                    type: SkeletonItemType.LINE,
                    width: 6,
                    height: 2,
                    style: {
                      backgroundColor: iconColor,
                      borderRadius: '1px',
                      transform: 'rotate(45deg)',
                      position: 'absolute',
                      top: '11px',
                      left: '8px',
                    },
                  },
                  {
                    type: SkeletonItemType.CIRCLE,
                    width: 10,
                    height: 10,
                    style: {
                      border: `1.8px solid ${iconColor}`,
                      backgroundColor: 'transparent',
                      position: 'absolute',
                      top: '2px',
                      left: '2px',
                    },
                  },
                ],
              },
              // Search input field
              {
                type: SkeletonItemType.BOX,
                width: '100%',
                height: 40,
                style: {
                  paddingLeft: '2.5rem', // ps-10 equivalent
                  backgroundColor: searchbarBackground,
                  border: `1px solid ${searchbarBorder}`,
                  borderRadius: '6px',
                  position: 'relative',
                },
              },
              // Placeholder text (positioned inside the input)
              // {
              //   type: 'line' as const,
              //   width: '60%',
              //   height: 14,
              //   style: {
              //     position: 'absolute',
              //     left: '2.5rem',
              //     top: '50%',
              //     transform: 'translateY(-50%)',
              //     backgroundColor: textColor,
              //     borderRadius: '3px',
              //     zIndex: 5,
              //   },
              // },
            ],
          },
        ],
      },
    ]
  },

  [CARD_PRESET.DROPDOWN_TEXT_FIELD]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }
    // Theme-specific colors
    const searchbarBackground = isDark ? '#1D1E24' : '#ffffff'
    const searchbarBorder = isDark ? '#374151' : '#e5e7eb'
    const iconColor = isDark ? '#A1A5B7' : '#6b7280'
    // const textColor = isDark ? '#A1A5B7' : '#9ca3af'

    return [
      {
        key: 'searchbar-root',
        direction: SkeletonDirection.ROW,
        alignItems: 'center',
        gap: 0,
        style: {
          // padding: '4px 26px', // px-8 equivalent
          backgroundColor: 'transparent',
          width: '100%',
        },
        items: [
          {
            key: 'searchbar-container',
            direction: SkeletonDirection.ROW,
            alignItems: 'center',
            gap: 0,
            style: {
              position: 'relative',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
            },
            items: [
              // Search input field
              {
                type: SkeletonItemType.BOX,
                width: '100%',
                height: 40,
                style: {
                  paddingLeft: '2.5rem', // ps-10 equivalent
                  backgroundColor: searchbarBackground,
                  border: `1px solid ${searchbarBorder}`,
                  borderRadius: '6px',
                  position: 'relative',
                },
              },
              // Placeholder text (positioned inside the input)
              // {
              //   type: 'line' as const,
              //   width: '60%',
              //   height: 14,
              //   style: {
              //     position: 'absolute',
              //     left: '2.5rem',
              //     top: '50%',
              //     transform: 'translateY(-50%)',
              //     backgroundColor: textColor,
              //     borderRadius: '3px',
              //     zIndex: 5,
              //   },
              // },

              // Search icon (positioned absolutely on the left)
              {
                key: 'dropdown-icon',
                direction: SkeletonDirection.ROW,
                alignItems: 'center',
                justifyContent: 'center',
                style: {
                  position: 'absolute',
                  right: '12px', // ms-3 equivalent
                  zIndex: 10,
                  width: 16,
                  height: 16,
                  color: iconColor,

                  fontSize: '14px',
                },
                items: [
                  {
                    type: SkeletonItemType.LINE,
                    width: 7,
                    height: 2,
                    style: {
                      backgroundColor: iconColor,
                      borderRadius: '1px',
                      transform: 'rotate(45deg)',
                      position: 'absolute',
                      top: '8px',
                      left: '3px',
                    },
                  },
                  {
                    type: SkeletonItemType.LINE,
                    width: 7,
                    height: 2,
                    style: {
                      backgroundColor: iconColor,
                      borderRadius: '1px',
                      transform: 'rotate(135deg)',
                      position: 'absolute',
                      top: '8px',
                      left: '7px',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ]
  },
  [CARD_PRESET.TEXT_FIELD]: (theme: ThemeVariant = 'auto'): SkeletonConfig => {
    // Determine if we should use dark theme
    let isDark = true // Default to dark
    if (theme === 'light') {
      isDark = false
    } else if (theme === 'dark') {
      isDark = true
    } else if (theme === 'auto') {
      // This will be handled by the component's useThemeMode hook
      const activeTheme = getTheme()
      isDark = activeTheme === 'dark'
    }
    // Theme-specific colors
    const searchbarBackground = isDark ? '#1D1E24' : '#ffffff'
    const searchbarBorder = isDark ? '#374151' : '#e5e7eb'
    // const iconColor = isDark ? '#A1A5B7' : '#6b7280'
    // const textColor = isDark ? '#A1A5B7' : '#9ca3af'

    return [
      {
        key: 'searchbar-root',
        direction: SkeletonDirection.ROW,
        alignItems: 'center',
        gap: 0,
        style: {
          // padding: '4px 26px', // px-8 equivalent
          backgroundColor: 'transparent',
          width: '100%',
        },
        items: [
          {
            key: 'searchbar-container',
            direction: SkeletonDirection.ROW,
            alignItems: 'center',
            gap: 0,
            style: {
              position: 'relative',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
            },
            items: [
              // Search input field
              {
                type: SkeletonItemType.BOX,
                width: '100%',
                height: 40,
                style: {
                  paddingLeft: '2.5rem', // ps-10 equivalent
                  backgroundColor: searchbarBackground,
                  border: `1px solid ${searchbarBorder}`,
                  borderRadius: '6px',
                  position: 'relative',
                },
              },
              // Placeholder text (positioned inside the input)
              // {
              //   type: 'line' as const,
              //   width: '60%',
              //   height: 14,
              //   style: {
              //     position: 'absolute',
              //     left: '2.5rem',
              //     top: '50%',
              //     transform: 'translateY(-50%)',
              //     backgroundColor: textColor,
              //     borderRadius: '3px',
              //     zIndex: 5,
              //   },
              // },
            ],
          },
        ],
      },
    ]
  },
}

//======================================================================
//  CSS ANIMATIONS - Modern, performant animations
//======================================================================

const getAnimationStyles = (
  animationType: AnimationType,
  theme: ThemeVariant,
  currentMode?: string
) => {
  // Determine if we should use dark theme
  let isDark = currentMode === 'dark'

  if (theme === 'dark') {
    isDark = true
  } else if (theme === 'light') {
    isDark = false
  } else if (theme === 'auto') {
    // Use the globally active theme when auto is requested
    const activeTheme = getTheme()
    isDark = (activeTheme || currentMode) === 'dark'
  }

  // Enhanced color palette for better contrast and modern look
  const baseColor = isDark ? '#374151' : '#e5e7eb'
  const highlightColor = isDark ? '#4b5563' : '#f3f4f6'
  const shimmerColor = isDark ? '#6b7280' : '#ffffff'

  switch (animationType) {
    case 'shimmer':
      return `
        @keyframes skeleton-shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        .skeleton-element {
          animation: skeleton-shimmer 1.5s ease-in-out infinite;
          background-color: ${baseColor};
          background-image: linear-gradient(90deg, ${baseColor}, ${shimmerColor}, ${baseColor});
          background-size: 200% 100%;
          background-repeat: no-repeat;
        }
      `

    case 'pulse':
      return `
        @keyframes skeleton-pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        .skeleton-element {
          animation: skeleton-pulse 2s ease-in-out infinite;
          background-color: ${baseColor};
        }
      `

    case 'wave':
      return `
        @keyframes skeleton-wave {
          0% {
            transform: translateX(-100%);
          }
          50% {
            transform: translateX(100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .skeleton-element {
          position: relative;
          background-color: ${baseColor};
          overflow: hidden;
        }
        
        .skeleton-element::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, ${highlightColor}, transparent);
          animation: skeleton-wave 2s ease-in-out infinite;
        }
      `

    default:
      return ''
  }
}

//======================================================================
//  COMPONENT PROPS
//======================================================================

type CustomSkeletonProps = {
  /** A pre-defined layout variant or 'custom' to provide your own config */
  variant: keyof typeof PRESETS | 'custom'
  /** The configuration object, required only if variant is 'custom' */
  config?: SkeletonConfig
  /** The number of times to repeat the skeleton layout */
  count?: number
  /** Animation type for skeleton elements */
  animation?: AnimationType
  /** Theme variant for skeleton colors */
  theme?: ThemeVariant
  /** Custom color overrides for specific elements */
  customColors?: CustomColors
  /** Optional class name for the root container */
  className?: string
  /** Optional style object for the root container */
  style?: CSSProperties
  /** Whether to show skeleton loading state */
  loading?: boolean
}

//======================================================================
//  THE SKELETON COMPONENT
//======================================================================

export const CustomSkeleton = ({
  variant,
  config,
  count = 1,
  animation = 'shimmer',
  theme = 'auto',
  customColors,
  className,
  style,
  loading = true,
}: CustomSkeletonProps) => {
  // Get current theme from the theme system
  const {mode} = useThemeMode()

  // Don't render anything if not loading
  if (!loading) {
    return null
  }

  // Determine which configuration to use
  let activeConfig: SkeletonConfig | undefined
  if (variant === 'custom') {
    activeConfig = config
  } else {
    const preset = PRESETS[variant]
    // Check if preset is a function (theme-aware) or static config
    if (typeof preset === 'function') {
      // Determine the effective theme
      let effectiveTheme = theme
      const activeTheme = getTheme()
      if (theme === 'auto') {
        effectiveTheme = activeTheme === 'dark' ? 'dark' : 'light'
      }
      activeConfig = preset(effectiveTheme, customColors)
    } else {
      activeConfig = preset
    }
  }

  if (!activeConfig) {
    console.error(
      `CustomSkeleton Error: Variant "${variant}" is not defined and no custom config was provided.`
    )
    return null
  }

  // Recursive function to render any element (item or row)
  const renderElement = (element: any, index: number) => {
    // Handle nested preset reference by key string
    if (typeof element === 'string') {
      const nestedPreset = (PRESETS as any)[element]
      if (nestedPreset) {
        // Resolve theme similar to top-level
        let nestedTheme: ThemeVariant = theme
        const activeTheme = getTheme()
        if (nestedTheme === 'auto') {
          nestedTheme = activeTheme === 'dark' ? 'dark' : 'light'
        }
        const nestedConfig: SkeletonConfig =
          typeof nestedPreset === 'function' ? nestedPreset(nestedTheme) : nestedPreset
        return (
          <div key={`nested-${index}`} style={{width: '100%'}}>
            {nestedConfig.map((el: any, i: number) => renderElement(el, i))}
          </div>
        )
      }
    }
    // Check if the element is a row (has an 'items' property)
    if ('items' in element) {
      const row = element
      const rowStyle: CSSProperties = {
        display: 'flex',
        flexDirection: row.direction || 'column',
        gap: row.gap,
        alignItems: row.alignItems,
        justifyContent: row.justifyContent,
        width: '100%',
        ...row.style,
      }
      return (
        <div key={row.key || index} style={rowStyle}>
          {row.items.map(renderElement)}
        </div>
      )
    }

    // Otherwise, it's a basic item
    const item = element
    const itemStyle: CSSProperties = {
      width: item.width,
      height: item.height,
      borderRadius: item.type === 'circle' ? '50%' : '4px', // Default radius, can be overridden
      ...item.style,
    }

    return <div key={index} className='skeleton-element' style={itemStyle} />
  }

  const skeletonLayout = <div style={{width: '100%'}}>{activeConfig.map(renderElement)}</div>

  return (
    <>
      <style>
        {getAnimationStyles(animation, theme, mode)}
        {/* Responsive styles for timetable skeleton */}
        {variant === CARD_PRESET.TIMETABLE_GRID &&
          `
          @media (max-width: 768px) {
            .skeleton-element {
              font-size: 0.75rem !important;
            }
            .timetable-skeleton-mobile {
              min-width: 280px !important;
            }
            .timetable-skeleton-mobile .skeleton-element {
              min-width: 80px !important;
              height: 3rem !important;
            }
          }
          @media (max-width: 480px) {
            .timetable-skeleton-mobile {
              min-width: 240px !important;
            }
            .timetable-skeleton-mobile .skeleton-element {
              min-width: 70px !important;
              height: 2.5rem !important;
            }
          }
        `}
      </style>
      {/* // eslint-disable-next-line @typescript-eslint/no-unused-vars */}
      {Array.from({length: count}).map((_, index) => (
        <div
          key={index}
          className={`${className} ${variant === CARD_PRESET.TIMETABLE_GRID ? 'timetable-skeleton-mobile' : ''}`}
          style={style}
          aria-hidden='true'
        >
          {skeletonLayout}
        </div>
      ))}
    </>
  )
}

// Export types for external use
export type {
  AnimationType,
  CustomColors,
  CustomSkeletonProps,
  SkeletonConfig,
  SkeletonItem,
  SkeletonRow,
  ThemeVariant,
}

// Export presets for external extension
export {PRESETS}

export default CustomSkeleton
